import {
    Text,
    Link,
    Heading,
    Grid,
    GridItem,
    HStack,
    VStack,
    Button,
    Box,
    Show,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    UnorderedList,
    ListItem,
    Spinner,
    Center,
} from "@chakra-ui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { QuestionOutlineIcon } from "@chakra-ui/icons";
import Map from "./components/Map";
import StatusSideBar from "./components/StatusSideBar";
import { fetchStations } from "./services/api";
import './App.css';

function App() {

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [data, setData] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    const controllerRef = useRef(null);

    const getData = useCallback(() => {
        if (controllerRef.current) controllerRef.current.abort();
        const controller = new AbortController();
        controllerRef.current = controller;
        fetchStations(controller.signal)
            .then(stations => {
                setData(stations);
                setError(null);
                setLastUpdated(new Date());
                setLoading(false);
            })
            .catch(err => {
                if (err?.code === 'ERR_CANCELED') return;
                console.error('Error al obtener datos:', err);
                setError('No se pudieron cargar los datos. Verifica tu conexión e intenta más tarde.');
                setData([]);
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        getData();
        const interval = setInterval(getData, 3600000);
        return () => {
            if (controllerRef.current) controllerRef.current.abort();
            clearInterval(interval);
        };
    }, [getData]);

    if (isLoading) {
        return (
            <Center width='100%' height='100vh'>
                <Spinner
                    height='150px'
                    width='150px'
                    thickness='4px'
                    speed='0.65s'
                    emptyColor='gray.200'
                    color='blue.500'
                    size='xl' />
            </Center>
        );
    }

    if (error) {
        return (
            <Center width='100%' height='100vh'>
                <VStack spacing={4} maxW='500px' px={4}>
                    <Alert status='error' borderRadius='md' flexDirection='column' textAlign='center'>
                        <AlertIcon boxSize={8} mr={0} mb={2} />
                        <AlertTitle>Error al cargar los datos</AlertTitle>
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                    <Button colorScheme='blue' onClick={() => { setLoading(true); getData(); }}>Reintentar</Button>
                </VStack>
            </Center>
        );
    }

    return (
        <Grid
            templateAreas={{
                base: `"header header"
                       "nav    main"`,
                lg: `"header header"
                     "nav    main"
                     "nav    footer"`
            }}
            gridTemplateRows={{ base: '60px 1fr', lg: '70px 1fr 30px' }}
            gridTemplateColumns={{ base: '0px 1fr', lg: '355px 1fr' }}
            h='100vh'
            color='blackAlpha.700'
            fontWeight='bold'>

            <GridItem pl='15px' pr='15px' bg='gray.100' area={'header'}>
                <HStack mt='13px' justify='space-between'>
                    <Heading size={{ base: 'md', lg: 'xl' }} noOfLines={1}>
                        <Link
                            color='teal.500'
                            href='https://sinca.mma.gob.cl/'
                            isExternal
                        >
                            Calidad del aire Chile
                        </Link>
                    </Heading>
                    <Button
                        onClick={onOpen}
                        size={{ base: 'sm', lg: 'md' }}
                        flexShrink={0}
                        aria-label='¿Qué se está midiendo?'
                    >
                        <Box display={{ base: 'none', md: 'inline' }}>¿Qué se está midiendo?</Box>
                        <QuestionOutlineIcon display={{ base: 'inline', md: 'none' }} />
                    </Button>
                </HStack>
            </GridItem>

            <GridItem bg='gray.400' area={'nav'}>
                <StatusSideBar data={data} lastUpdated={lastUpdated} />
            </GridItem>

            <GridItem area={'main'}>
                <Map stationsData={data} />
            </GridItem>

            <Show above='lg'>
                <GridItem pl='2' bg='gray.600' area={'footer'} color='white'>
                    <HStack>
                        <Text marginTop='3px'>
                            <Link
                                color='teal.500'
                                href='https://github.com/iwayato'
                                isExternal
                            >
                                Tomoaki Iwaya Villalobos
                            </Link>
                        </Text>
                        <Text>-</Text>
                        <Text>{new Date().getFullYear()}</Text>
                    </HStack>
                </GridItem>
            </Show>

            <Modal size={{ base: 'full', xl: 'xl' }} scrollBehavior="inside" isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Contaminantes</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Heading size='sm'>Material particulado</Heading>
                        <Text align='justify'>
                            Contaminante atmosférico que corresponde a aquellas partículas líquidas o sólidas
                            que se encuentran en suspensión, siendo posible clasificarlo según su diámetro en
                            MP10 (grueso) y MP2,5 (fino). El primero es aquel en que las partículas tienen un
                            diámetro menor a 10 micrones (o micrómetros), y el segundo, en que las partículas
                            tienen un diámetro menor a 2,5 micrones. Por lo mismo, el MP2,5 se encuentra
                            contenido en el MP10. También existe el denominado MP ultrafino, de alrededor
                            de 0,1 μm de diámetro. La concentración de MP es el valor promedio temporal
                            detectado en el aire y se mide en microgramos por metro cúbico normal (μg/m3N).
                        </Text>
                        <br />
                        <Heading size='sm'>Ozono troposférico</Heading>
                        <Text align='justify'>
                            Gas incoloro que se crea a través de reacciones fotoquímicas entre óxidos de nitrógeno
                            (NOx) y compuestos orgánicos volátiles (COV) derivados de fuentes como la quema de
                            combustible. Esto se convierte en un problema, puesto que el ozono, en concentración
                            suficiente, puede provocar daños en la salud humana (a partir de unos 150 microgramos
                            por metro cúbico) o en la vegetación (a partir de 30 ppb (partes por billón anglosajón,
                            o millardo) y contribuye a generar un calentamiento en la superficie de la Tierra.
                            Cabe destacar que el mecanismo mediante el cual se genera el ozono en la tropósfera
                            es completamente distinto, ya que depende de contaminantes provenientes de la
                            actividad humana, mientras que el ozono estratosférico se forma por la acción de la
                            radiación ultravioleta.
                        </Text>
                        <br />
                        <Heading size='sm'>Dióxido de azufre</Heading>
                        <Text align='justify'>
                            Gas irritante y tóxico. Afecta sobre todo las mucosidades y los pulmones provocando
                            ataques de tos. Si bien este es absorbido principalmente por el sistema nasal,
                            la exposición de altas concentraciones por cortos períodos de tiempo puede irritar
                            el tracto respiratorio, causar bronquitis y congestionar los conductos bronquiales
                            de los asmáticos. La concentración máxima permitida en los lugares de trabajo
                            es de 2 ppm. Es el principal causante de la lluvia ácida.
                        </Text>
                        <br />
                        <Heading size='sm'>Dióxido de nitrógeno</Heading>
                        <Text align='justify'>
                            Es un gas tóxico, irritante y precursor de la formación de partículas de nitrato.
                            Estas llevan a la producción de ácido y elevados niveles de PM-2.5 en el ambiente.
                            Afecta principalmente al sistema respiratorio.
                        </Text>
                        <br />
                        <Heading size='sm'>Monóxido de carbono</Heading>
                        <Text align='justify'>
                            Gas incoloro y altamente tóxico. Puede causar la muerte cuando se respira en niveles
                            muy elevados (0,4% de concentración en el aire). Se produce por la combustión deficiente
                            de sustancias como gas, gasolina, queroseno, carbón, petróleo, tabaco o madera.
                            Las chimeneas, las calderas, los calentadores de agua o calefactores y los aparatos
                            domésticos que queman combustible, como las estufas u hornillas de la cocina o los
                            calentadores a queroseno, también pueden producirlo si no están funcionando bien.
                        </Text>
                        <br />
                        <Heading size='sm'>Fuentes</Heading>
                        <UnorderedList>
                            <ListItem>
                                <Link href='https://airechile.mma.gob.cl/faq' color='teal.500' isExternal>
                                    Aire Chile
                                </Link>
                            </ListItem>
                            <ListItem>
                                <Link href='https://es.wikipedia.org/wiki/Ozono' color='teal.500' isExternal>
                                    Ozono
                                </Link>
                            </ListItem>
                            <ListItem>
                                <Link href='https://es.wikipedia.org/wiki/Di%C3%B3xido_de_azufre' color='teal.500' isExternal>
                                    Dióxido de azufre
                                </Link>
                            </ListItem>
                            <ListItem>
                                <Link href='https://es.wikipedia.org/wiki/Di%C3%B3xido_de_nitr%C3%B3geno' color='teal.500' isExternal>
                                    Dióxido de nitrógeno
                                </Link>
                            </ListItem>
                            <ListItem>
                                <Link href='https://es.wikipedia.org/wiki/Mon%C3%B3xido_de_carbono' color='teal.500' isExternal>
                                    Monóxido de carbono
                                </Link>
                            </ListItem>
                        </UnorderedList>
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme='blue' mr={0} onClick={onClose}>
                            Cerrar
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Grid>
    );
}

export default App;
